import { InverterDeviceType, PhaseType } from './firebase.model';

export type ProductType =
  | {
      name: 'inverter';
      value?: InverterDeviceType;
      phase?: PhaseType;
      imgName?: string;
      /** power in watts*/
      ratedPower?: number;
      productionDate?: Date;
    }
  | {
      name: 'meter';
      value: PhaseType;
      imgName?: string;
    }
  | {
      name: 'PSC100';
      imgName?: string;
    }
  | {
      name: 'evcharge';
      value?: PhaseType;
      imgName?: string;
    }
  | {
      name: 'heatPump';
      value?: PhaseType;
      imgName?: string;
    }
  | {
      name: 'azzurro-hub';
      value?: PhaseType;
      imgName?: string;
    }
  | {
      name: 'connext';
      value?: PhaseType;
      imgName?: string;
    }
  | {
      name: 'enerclic';
      value: PhaseType;
      imgName?: string;
    }
  | {
      name: 'no-zcs';
      imgName?: string;
    };

  export type productWithProperties = {
    product: ProductModelService,
    prettyName: string,
    properties: productProperty[],
  }

  export type productProperty = {
    name: string,
    value?: string,
    icon?: string,
    cssClasses?: string,
    iconCssClasses?: string,
  }

  export type propertyLabels = {
    deviceType: string;
    evCharge: string;
    hybridInverter: string;
    inverter: string;
    phase: string;
    photovoltaicInverter: string;
    ratedPower: string;
    serialNumber: string;
    singlePhase: string;
    stickLogger: string;
    storageInverter: string;
    threePhase: string;
    title: string;
    localeCode: string;
  };

  export const ONE_AND_ALL_NAME = 'One and All';

  export const POWER_MAGIC_NAME = 'Power Magic';

const isEvcharge = (id: string) =>
  id.length > 15 && id.substring(0, 2) === 'SN';
// ? find the correct enerclic code start with BBB and the correct code length
const isEnerclic = (id: string) => id.startsWith('BBB') && id.length > 5;
const isHeatPump = (id: string) =>
  id.length === 21 && id.match(/^\d+$/);
const isMeter = (id: string) =>
  id.length === 10 || (id.length === 16 && id.match(/(\d+)\-(\d+)/));
const isConnextWithProcessorID = (id: string) => id.length === 24;
const isAzzurroHub = (id: string) => id.substring(0, 2) === 'AH' && id.length === 12;
const isConnextWithProductID = (id: string) => {
  if (id.length === 14 && id.substring(0, 1) === 'C') {
    const hardwareRev = id.substring(1, 2).charCodeAt(0);
    const prodYear = Number(id.substring(3, 5));
    if (hardwareRev <= 90 && hardwareRev >= 65 && prodYear >= 20) {
      return true;
    }
  }
  return false;
};
const isPcs100 = (id: string) => id.length === 20 && id.substring(3, 6) === '019';
const isNoZcsDevice = (id: string) =>
  id.length === 27 && id.match(/(XX.{24}X)||(YY.{24}Y)/);
const isInverter = (id: string) => id.length === 14 || id.length === 20;

export class ProductModelService {
  type?: ProductType;
  id: string;
  inverterProductType?: 'A' | 'B' | 'C' | 'D';

  get isProductD() {
    return this.inverterProductType === 'D';
  }

  get isProductC() {
    return this.inverterProductType === 'C';
  }

  get isProductB() {
    return this.inverterProductType === 'B';
  }

  get isProductA() {
    return this.inverterProductType === 'A';
  }

  get isOneAndAll() {
    return (this.id.substring(3, 6) === '025' || this.id.substring(3, 6) === '057') &&
      this.type?.name === 'inverter' &&
      this.id.length === 20;
  }

  get isPowerMagic() {
    return this.id.substring(3, 6) === '011' &&
     this.type?.name === 'inverter' &&
      this.id.length === 20;
  }

  constructor(id: string) {
    this.id = id.toUpperCase();

    if (isEnerclic(this.id)) {
      this.type = {
        name: 'enerclic',
        value: 'mono-phase',
        imgName: 'monitoring/combox.svg',
      };
    } else if (isEvcharge(this.id)) {
      this.type = {
        name: 'evcharge',
        imgName: 'other/wallbox.svg',
      };
    } else if (isMeter(this.id)) {
      this.type = {
        name: 'meter',
        value: 'mono-phase',
        imgName: 'monitoring/meter.svg',
      };
    } else if (isHeatPump(this.id)) {
      this.type = {
        name: 'heatPump',
        imgName: 'monitoring/heat-pump.svg',
      };
    } else if (isAzzurroHub(this.id)) {
      this.type = {
        name: 'azzurro-hub',
        imgName: 'monitoring/azzurro-hub.svg',
      };
    } else if (isConnextWithProcessorID(this.id)) {
      this.type = {
        name: 'connext',
        imgName: 'monitoring/connext.svg',
      };
    } else if (isNoZcsDevice(this.id)) {
      this.type = {
        name: 'no-zcs',
        imgName: 'other/otherDevice.svg',
      };
    } else if (isConnextWithProductID(this.id)) {
      this.type = {
        name: 'connext',
        imgName: 'monitoring/connext.svg',
      };
    } else if (isPcs100(this.id)) {
      this.type = {
        name: 'PSC100',
        imgName: 'inverters/ZH0019.svg',
      };
    } else if (isInverter(this.id)) {
      this.type = {
        name: 'inverter',
      };
    }
    if (this.type === undefined) {
      // unknown type
      return;
    }

    if (this.type.name === 'inverter') {
      if (this.id.length === 14) {
        this.decodeInverterSerialV0();
      } else if (this.id.length === 20) {
        this.decodeInverterSerialV1();
      } else {
        return;
      }
    }
  }

  decodeInverterSerialV0() {
    // Producer
    const prod = this.id.substring(0, 1);
    if (prod === 'S') {
      // Sofar
    } else if (prod === 'Z') {
      // Zucchetti
    } else {
      // GOTO ERROR HANDLER
      return;
    }
    // Family
    const family = this.id.substring(1, 2);
    const category = this.id.substring(2, 3);
    // location = this.id.substring(3, 4);
    const accessory = this.id.substring(4, 5);
    const power = this.id.substring(5, 8);
    const productionDate = this.id.substring(8, 11);
    if (this.type?.name === 'inverter') {
      this.type.imgName = `DefaultInverter.svg`;
      this.type.productionDate = getProductionDate(productionDate);
      this.type.ratedPower = getRatedPower(family, category, accessory, power);
      switch (family) {
        case 'C': // Three-phase 10-20kW
        case 'L': // Three-phase 20-30kW
        case 'D': // Three-phase 30-40kW
        case 'G': // Three-phase 30-40kW
        case 'N': // Three-phase 30-40kW
        case 'F': // Three-phase series compact 4-12kW
          this.type.phase = 'three-phase';
          this.type.value = 'pv';
          this.inverterProductType = 'A';
          this.type.imgName = `Z${family}.svg`;
          break;
        case 'I': // Three-phase 50-70kW
        case 'J': // Three-phase 50-70kW
          this.inverterProductType = 'B';
          this.type.phase = 'three-phase';
          this.type.value = 'pv';
          this.type.imgName = `Z${family}.svg`;
          break;
        case 'Q': // Three-phase
          this.inverterProductType = 'D';
          this.type.phase = 'three-phase';
          this.type.value = 'pv';
          this.type.imgName = `Z${family}.svg`;
          break;
        case 'R': // Three-phase
          this.inverterProductType = 'D';
          this.type.phase = 'three-phase';
          this.type.value = 'pv';
          break;
        case 'A': // Mono-phase single MPPT 1-3KW
          this.inverterProductType = 'A';
          this.type.phase = 'mono-phase';
          this.type.value = 'pv';
          this.type.imgName = `Z${family}${category}.svg`;
          break;
        case 'B': // Mono-phase double MPPT 3-6KW
          this.inverterProductType = 'A';
          this.type.phase = 'mono-phase';
          this.type.value = 'pv';
          break;
        case 'H': // Mono-phase double Lite MPPT 3-6KW
          this.type.phase = 'mono-phase';
          this.type.value = 'pv';
          if (category === '1') {
            // ZH1
            this.inverterProductType = 'A';
            this.type.imgName = `Z${family}${category}.svg`;
            break;
          } else if (category === '3') {
            // ZH3
            this.inverterProductType = 'D';
            this.type.imgName = `Z${family}${category}.svg`;
            break;
          }
          break;
        case 'S':
          this.type.phase = 'three-phase';
          this.inverterProductType = 'D';
          this.type.value = 'pv';
          this.type.imgName = `Z${family}.svg`;
          break;
        case 'M': // Hybrid
          if (category === '1') {
            // M1
            this.type.phase = 'mono-phase';
            this.type.value = 'hybrid';
            this.inverterProductType = 'C';
            this.type.imgName = `Z${family}${category}.svg`;
            break;
          } else if (category === '2') {
            // M2
            this.type.phase = 'mono-phase';
            this.type.value = 'hybrid-3ph';
            this.inverterProductType = 'D';
            this.type.imgName = `Z${family}${category}.svg`;
            break;
          }
          break;
        case 'E': // Storage ME 3000SP
          this.type.phase = 'mono-phase';
          this.type.value = 'storage';
          this.inverterProductType = 'C';
          this.type.imgName = `Z${family}.svg`;
          break;
        case 'P': // Three-phase
          this.type.phase = 'three-phase';
          this.type.value = 'hybrid-3ph';
          this.inverterProductType = 'D';
          this.type.imgName = `Z${family}.svg`;
          break;
        default:
          // unknown type
          return;
      }
      this.type.imgName = 'inverters/' + this.type.imgName;
    }
  }

  decodeInverterSerialV1() {
    // Producer
    const prod = this.id.substring(0, 1);
    if (prod === 'Z') {
      // Zucchetti
    } else {
      // Sofar
    }
    const family = this.id.substring(1, 2);
    const projectCode = this.id.substring(3, 6);
    if (this.type?.name === 'inverter') {
      this.type.imgName = `DefaultInverter.svg`;
      switch (projectCode) {
        case '008':
          this.type.phase = 'mono-phase';
          this.type.value = 'storage';
          this.inverterProductType = 'D';
          this.type.imgName = `ZH008.svg`;
          break;
        case '021':
          this.type.phase = 'three-phase';
          this.type.value = 'pv';
          this.inverterProductType = 'D';
          this.type.imgName = `ZQ.svg`;
          break;
        case '030':
          this.type.phase = 'three-phase';
          this.type.value = 'pv';
          this.inverterProductType = 'D';
          this.type.imgName = `ZH1030.svg`;
          break;
        case '011':
          this.type.phase = 'three-phase';
          this.type.value = 'storage';
          this.inverterProductType = 'C';
          this.type.imgName = `ZH011.svg`;
          break;
        case '036':
          this.type.phase = 'three-phase';
          this.type.value = 'pv';
          this.inverterProductType = 'D';
          this.type.imgName = `ZQ.svg`;
          break;
        case '050':
          this.type.phase = 'mono-phase';
          this.type.value = 'hybrid-3ph';
          this.inverterProductType = 'D';
          this.type.imgName = `ZM2.svg`;
          break;
        case '063':
          this.type.phase = 'three-phase';
          this.type.value = 'hybrid-3ph';
          this.inverterProductType = 'D';
          this.type.imgName = `ZP.svg`;
          break;
        case '025':
          //One and all 1PH
          this.type.phase = 'mono-phase';
          this.type.value = 'hybrid-3ph';
          this.inverterProductType = 'D';
          this.type.imgName = `OneAndAll.svg`;
          break;
        case '057':
          //One and all 3PH
          this.type.phase = 'three-phase';
          this.type.value = 'hybrid-3ph';
          this.inverterProductType = 'D';
          this.type.imgName = `OneAndAll.svg`;
          break;
        default:
          return;
      }
      this.type.imgName = 'inverters/' + this.type.imgName;
    }
  }
}

const getRatedPower = (family: string, category: string, accessory: string, power: string): number | undefined => {
  switch (family) {
    case 'A': // Mono-phase single MPPT 1-3KW
      const shortPowerFigure = power.substring(1, 3);
      return Number(shortPowerFigure) * 100;
    case 'B':
    case 'H':
      switch (power) {
        case '130':
          return 3000;
        case '136':
          return 3680;
        case '140':
          return 4000;
        case '146':
          return 4600;
        case '150':
          return 5000;
        case '160':
          return 6000;
      }
      break;
    case 'F':
      switch (power) {
        case '003':
          return 3300;
        case '004':
          return 4400;
        case '005':
          return 5500;
        case '006':
          return 6600;
        case '008':
          return 8800;
        case '010':
          return 11000;
        case '012':
          return 12000;
      }
      break;
    case 'S':
      switch (power) {
        case '003':
          return 3300;
        case '004':
          return 4400;
        case '005':
          return 5500;
        case '006':
          return 6600;
        case '008':
          return 8800;
        case '010':
          return 11000;
        case '012':
          return 12000;
        case '015':
          return 15000;
        case '017':
          return 17000;
        case '020':
          return 20000;
        case '022':
          return 22000;
        case '024':
          return 24000;
      }
      break;
    case 'N':
      switch (power) {
        case '010':
          return 10000;
        case '012':
          return 12000;
        case '015':
          return 15000;
      }
      break;
    case 'L':
      switch (power) {
        case '020':
          return 20000;
        case '025':
          return 25000;
        case '030':
          return 30000;
        case '033':
          return 33000;
      }
      break;
    case 'J':
      switch (power) {
        case '050':
          return 50000;
        case '060':
          return 60000;
      }
      break;
    case 'Q':
      switch (accessory) {
        case 'S':
          switch (power) {
            case '080':
              return 80000;
            case '0A0':
              return 100000;
            case '0B0':
              return 110000;
          }
          break;
        case 'H':
          switch (power) {
            case '080':
              return 100000;
            case '0C5':
              return 125000;
            case '0D6':
              return 136000;
          }
          break;
      }
      break;
    case 'P':
      switch (power) {
        case '005':
          return 5000;
        case '006':
          return 6000;
        case '008':
          return 8000;
        case '010':
          return 10000;
        case '015':
          return 15000;
        case '020':
          return 20000;
      }
      break;
    case 'M':
      switch (category) {
        case '1':
          switch (power) {
            case '030':
              return 3000;
            case '036':
              return 3600;
            case '040':
              return 4000;
            case '046':
              return 4600;
            case '050':
              return 5000;
            case '060':
              return 6000;
          }
          break;
        case '2':
          switch (power) {
            case '030':
              return 3000;
            case '036':
              return 3600;
            case '040':
              return 4000;
            case '046':
              return 4600;
            case '050':
              return 5000;
            case '060':
              return 6000;
          }
          break;
      }
  }
  return undefined;
}

const getProductionDate = (input: string): Date => {
  input = input.toUpperCase()
  const yearChar = input.substring(0, 1)
  const monthChar = input.substring(1, 2)
  const dayChar = input.substring(2, 3)

  //Starts at Jan-2016 (G1)
  const YearChars = ['G', 'H', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']
  const monthChars = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C']
  const dayChars = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V']

  const yearIndex = YearChars.indexOf(yearChar);
  const monthIndex = monthChars.indexOf(monthChar);
  const dayIndex = dayChars.indexOf(dayChar);

  return new Date(`20${16 + yearIndex}-${monthIndex + 1}-${dayIndex + 1}`)
}

export const getDeviceIconIdentifier = (
  deviceId: string,
):
  | 'customer_default_device'
  | 'customer_inverter'
  | 'customer_evcharge'
  | 'customer_meter'
  | 'customer_combox'
  | 'customer_connext'
  | 'customer_azzurro_hub'
  | 'customer_heat_pump' => {
  const product = new ProductModelService(deviceId);
  if (
    product.type?.name &&
    ['inverter', 'meter', 'enerclic', 'evcharge', 'connext', 'azzurro-hub', 'heatPump'].includes(product.type?.name)
  ) {
    switch (product.type?.name) {
      case 'inverter':
        return `customer_inverter`;
      case 'meter':
        return `customer_meter`;
      case 'enerclic':
        return `customer_combox`;
      case 'evcharge':
        return 'customer_evcharge';
      case 'connext':
        return 'customer_connext';
      case 'azzurro-hub':
        return 'customer_azzurro_hub';
      case 'heatPump':
        return 'customer_heat_pump';
    }
  }
  return 'customer_default_device';
};

export enum InverterModelType {
  Unknown = 0,
  PV,
  HybridOrStorage,
}

export const getInverterType = (serial?: string) => {
  if (!serial) {
    return InverterModelType.Unknown;
  }

  const product = new ProductModelService(serial);

  if (product.type?.name !== 'inverter') {
    return InverterModelType.Unknown;
  }

  if (product.type.value === 'pv') {
    return InverterModelType.PV;
  }

  return InverterModelType.HybridOrStorage;
};
